import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { Status } from '../../../hooks/useFetchState'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../SnackBar/Snackbar'
import Grid from '@mui/material/Grid2'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { CheckboxWithLabel, TextField } from 'formik-mui'
import { positionValidationSchema } from '../types/positionConfig'
import { ICreatePosition } from '../../../interfaces/Position/Position'
import { Zone } from '../../../interfaces'
import { useWarehouses, useZones } from '../../../hooks'
import { eTypePosition } from '../../../enums/eTypePosition'
import { createPosition, getTypePositionsDDL, updatePosition } from '../api'
import { GenericDLL } from '../../Almacen/UI/GenericDLL'
import { Loading } from '../../UI'

export interface CreatePositionHandle {
  submitForm: () => void
  isSubmitting: boolean
  isValid: boolean
  isLoading: boolean
}

interface CreatePositionProps {
  editableItem?: ICreatePosition & { warehouseId?: number; externalStorage?: boolean }
  onSuccess?: () => void
}

const initialValues = {
  id: 0,
  warehouseId: '' as unknown as number,
  positionName: '',
  positionDescription: '',
  subZoneId: undefined,
  zoneId: undefined,
  typePositionId: '' as unknown as number,
  active: true,
  externalStorage: false
}

export const CreatePosition = forwardRef<CreatePositionHandle, CreatePositionProps>(({ editableItem, onSuccess }, ref) => {
  const formikRef = useRef<any>(null)
  const { isActive, message, isSuccess: isSnackSuccess, openSnackBar } = useSnackbar()
  const { callApi, state: apiState } = useApi()
  const [requiredZone, setRequiredZone] = useState<boolean>(false)
  const [zonesDDL, setZonesDDL] = useState<Zone[]>([])
  const [typePositionItems, setTypePositionItems] = useState<any[]>([])

  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm(),
    get isSubmitting() { return formikRef.current?.isSubmitting ?? false },
    get isValid() { return formikRef.current?.isValid ?? false },
    get isLoading() { return apiState.status === Status.Loading }
  }), [apiState.status])

  const {
    zonesState: { zones },
    getZones
  } = useZones()

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()


  const handleSelectedWarehouseZones = (warehouseId: number, zones: Zone[]) => {
    if (warehouseId > 0) {
      const filteredZones = [...zones!].filter((x: Zone) => x.warehouseId === warehouseId)
      setZonesDDL(filteredZones)
    }
  }

  const validateSelectedTypePosition = (typePosition: number) => {
    if (typePosition > 0) {
      const match = typePositionItems.find((x) => x.typePositionId === typePosition)
      const fieldValue = match?.requireZone ?? false
      setRequiredZone(fieldValue)
      return fieldValue
    }
    return false
  }

  useEffect(() => {
    if (warehouses?.length === 0) getWarehouses()
    if (zones?.length === 0) getZones()
    getTypePositionsDDL()
      .then((response) => response.data)
      .then((data) => setTypePositionItems(data?.items ?? data))
  }, [])


  useEffect(() => {
    if (editableItem?.warehouseId && zones?.length) {
      handleSelectedWarehouseZones(editableItem.warehouseId, zones)
    }
  }, [editableItem, zones])

  const handleZoneChange = (
    value: number,
    setFieldValue: (field: string, value: any) => void,
    zonesDDL: Zone[]
  ) => {
    setFieldValue('zoneId', value)
    setFieldValue('subZoneId', undefined)
    const selectedZone = zonesDDL.find((z) => z.zoneId === Number(value))
    const isExternal = selectedZone ? !!selectedZone.externalStorage : false
    setFieldValue('externalStorage', isExternal)
  }

  const shouldShowTypePosition = (
    isExternalZone: boolean,
    typePositionId: number,
    isEditable: boolean
  ): boolean => {
    const isReserva = typePositionId === eTypePosition.Reserva
    return (isExternalZone && isReserva && isEditable) || (!isReserva && isEditable)
  }

  const isLoading =
    !warehouses?.length || !zones?.length || !typePositionItems.length

  const handleSubmit = async (values: any) => {
    await callApi(createPosition(values))
      .then(() => { openSnackBar('Posición creada correctamente', true); onSuccess?.() })
      .catch((error) => openSnackBar(error, false))
  }

  const handleSubmitEdit = async (values: any) => {
    await callApi(updatePosition(values))
      .then(() => { openSnackBar('Posición actualizada correctamente', true); onSuccess?.() })
      .catch((error) => openSnackBar(error, false))
  }

  if (isLoading || apiState.status === Status.Loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <SnackBar message={message} status={isSnackSuccess ? 'success' : 'error'} open={isActive} />
      <Formik
        innerRef={formikRef}
        initialValues={editableItem && editableItem.id ? editableItem : initialValues}
        onSubmit={async (values) =>
          editableItem ? handleSubmitEdit(values) : handleSubmit(values)
        }
        validationSchema={positionValidationSchema(requiredZone)}
      >
        {({ values, setFieldValue }) => (
          <Form autoComplete='off'>
            <Grid container direction='row' columnSpacing={2} rowSpacing={0}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field
                  fullWidth
                  name='positionName'
                  component={TextField}
                  label='Nombre Posición'
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <GenericDLL
                  fullWidth
                  initialValues={typePositionItems.filter(({ typePositionId, canCreatePosition }) =>
                    shouldShowTypePosition(!!values.externalStorage, typePositionId, canCreatePosition)
                  )}
                  valueKey='typePositionId'
                  name='typePositionDescription'
                  title='Tipo Posición'
                  label='Tipo Posición'
                  variant='outlined'
                  value={values.typePositionId || -1}
                  onChange={(e: any) => {
                    const { value } = e.target
                    validateSelectedTypePosition(value)
                    setFieldValue('typePositionId', value)
                  }}
                />
                <p style={{ color: '#f44336', fontSize: '13px' }}>
                  <ErrorMessage name='typePositionId' />
                </p>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <GenericDLL
                  fullWidth
                  initialValues={warehouses || []}
                  valueKey='id'
                  name='warehouseName'
                  title='Almacén'
                  label='Almacén'
                  variant='outlined'
                  value={values.warehouseId || -1}
                  onChange={(e: any) => {
                    const { value } = e.target
                    setFieldValue('warehouseId', value)
                    handleSelectedWarehouseZones(value, zones!)
                    setFieldValue('zoneId', undefined)
                    setFieldValue('subZoneId', undefined)
                    setFieldValue('externalStorage', value)
                  }}
                />
                <p style={{ color: '#f44336', fontSize: '13px' }}>
                  <ErrorMessage name='warehouseId' />
                </p>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <GenericDLL
                  fullWidth
                  initialValues={zonesDDL}
                  valueKey='zoneId'
                  name='zoneName'
                  title='Zona'
                  label='Zona'
                  variant='outlined'
                  value={values.zoneId || -1}
                  onChange={(e: any) => {
                    const val = e.target.value === -1 ? undefined : e.target.value
                    handleZoneChange(val, setFieldValue, zonesDDL)
                  }}
                />
                <p style={{ color: '#f44336', fontSize: '13px' }}>
                  <ErrorMessage name='zoneId' />
                </p>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Field
                  name='active'
                  component={CheckboxWithLabel}
                  Label={{ label: 'Activo' }}
                  type='checkbox'
                  color='default'
                  checked={values.active}
                  onChange={async (e: any) => {
                    const { checked } = e.target
                    setFieldValue('active', checked)
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Field
                  fullWidth
                  name='positionDescription'
                  component={TextField}
                  label='Descripción Posición'
                  multiline
                  rows={3}
                  variant='outlined'
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
})
